import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useSocket } from '../../contexts/SocketContext';
import axios from 'axios';
  import { useVoiceAnnouncer } from '../../hooks/useVoiceAnnouncer';
  import { RecallModal } from '../../components/RecallModal';
  import ServiceGrid from '../../components/ServiceGrid';
  import AdSlideshow from '../../components/AdSlideshow';
  import AdErrorBoundary from '../../components/AdErrorBoundary';
import AdBanner from '../../components/AdBanner';
  import useThemeCSS from '../../hooks/useThemeCSS';
  import { orderedServiceLangs } from '../../utils/serviceNameLangs';
  import { ticketLabel, libelleEcran, langueCodeTicket } from '../../utils/serviceCode';
  import { useFrontVersion } from '../../hooks/useFrontVersion';




/**
 * Écran d'affichage pour la salle d'attente
 * Affiche les tickets appelés et les guichets correspondants
 */
const DisplayScreen = () => {
  const { config, loading, error } = useConfig();
  const { socket, connected, registerClient } = useSocket();

  // Charge les feuilles de style du THÈME ACTIF pour l'écran d'affichage.
  // C'est ici que vivent les tailles des carrés de services (.service-count-item,
  // .service-count…) : voir le display-screen.css du thème. Auparavant /display ne
  // chargeait aucun CSS de thème → carrés non stylés (trop petits).
  useThemeCSS('common.css');
  useThemeCSS('display-screen.css');
  useThemeCSS('alert-animations.css');

  // États
  const [currentDate, setCurrentDate] = useState(new Date());
  const [servingTickets, setServingTickets] = useState([]);
  const [waitingCounts, setWaitingCounts] = useState({});
  // NB : `lastCalled` / `showCallAnimation` / `callAlert` sont conservés en écriture
  // (les handlers les mettent à jour) mais ne pilotent plus d'affichage : l'UNIQUE popup
  // est désormais `RecallModal` (piloté par `recallModalData`). Les anciens popups blancs
  // (AlertNotification + bannière inline) ont été retirés — voir plus bas.
  const [, setLastCalled] = useState(null);
  const [, setShowCallAnimation] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  // États pour les alertes et les sons
  const [, setCallAlert] = useState(null);
  const [recallModalData, setRecallModalData] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Fonction stable pour fermer la modale de rappel
  const closeRecallModal = useCallback(() => {
    console.log('🔵 DisplayScreen: Fermeture de la modale réclamée');
    setRecallModalData(null);
  }, []);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [language, setLanguage] = useState('fr-FR');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  // DOIT démarrer à `false` : la politique autoplay du navigateur bloque `<audio>.play()` ET
  // `speechSynthesis.speak()` tant que la page n'a reçu AUCUN geste utilisateur. Initialisé à
  // `true`, l'effet de déverrouillage (`if (audioUnlocked) return;`) ne s'armait jamais et la
  // bannière « activer le son » restait invisible → le PREMIER appel était muet, et la voix ne
  // repartait qu'après un clic fortuit dans la fenêtre (d'où « on n'entend que le rappel »).
  // Le chemin voix appel/rappel est identique par ailleurs (announceTicketMultilang /
  // announceRecallMultilang) : c'est bien le déverrouillage audio qui manquait, pas l'annonce.
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // Un écran allumé des semaines doit repartir sur le front à jour après réinstallation.
  useFrontVersion();

  const speechSynthesisRef = useRef(null);

  // ─── Plein écran automatique sur inactivité ───────────────────────────────
  // Après 10 s sans activité, /display passe en plein écran ; tout mouvement de
  // souris ou la touche Échap le ramène à sa taille et relance le compte à rebours.
  useEffect(() => {
    const FS_DELAY = 10000;
    let idleTimer = null;
    let enteredAt = 0;
    const inFs = () => !!document.fullscreenElement;
    const enterFs = () => {
      if (!inFs() && document.documentElement.requestFullscreen) {
        enteredAt = Date.now();
        document.documentElement.requestFullscreen().catch(() => {});
      }
    };
    const exitFs = () => {
      if (inFs() && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
    const arm = () => { clearTimeout(idleTimer); idleTimer = setTimeout(enterFs, FS_DELAY); };
    const onMove = () => {
      if (inFs() && Date.now() - enteredAt < 600) return; // ignore le mousemove parasite post-entrée
      exitFs();
      arm();
    };
    const onKey = (e) => { if (e.key === 'Escape') exitFs(); arm(); };
    const onFsChange = () => { if (!inFs()) arm(); }; // sortie (Échap natif…) → relance le délai

    arm();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      clearTimeout(idleTimer);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, []);
    // effectuer une mise à jour de l'interface utilisateur le rafrichissement de l'écran
  const updateTimeoutRef = useRef(null);

  // Mode pub / inactivité
  const [isIdle, setIsIdle] = useState(false);
  // Registre d'affichages (§5.bis) : filtre de services poussé par la console ([] = tous).
  const displayServicesRef = useRef([]);
  // Son (bip + annonces) poussé par la console, INDÉPENDANT par écran (activable sur
  // plusieurs à la fois). Défaut MUET : un nouvel écran ne sonne pas tant que la console
  // ne l'a pas activé → pas de cacophonie à l'ajout (facilite la mise en place).
  const displaySoundRef = useRef(false);
  // Approbation (facturation) : l'écran n'affiche la file QUE si l'éditeur l'a approuvé.
  // Défaut true (rétro-compat) ; displayConfig bascule à false pour un écran non approuvé.
  const [displayEnabled, setDisplayEnabled] = useState(true);
  // État du quota de licence poussé par le serveur — sert uniquement à EXPLIQUER l'attente.
  const [quotaEcrans, setQuotaEcrans] = useState(null);
  // Coupure ASSEZ LONGUE pour valoir la peine d'être signalée (cf. le bandeau, plus bas).
  const [coupureVisible, setCoupureVisible] = useState(false);
  const [myDisplayId, setMyDisplayId] = useState('');
  const idleTimerRef = useRef(null);

  // Hook de synthèse vocale — UNE seule instance (une seule file d'annonces).
  const voiceAnnouncer = useVoiceAnnouncer({ enabled: true });

  // Le bandeau de coupure n'apparaît qu'après 6 s d'absence, et disparaît dès le retour.
  useEffect(() => {
    if (connected) { setCoupureVisible(false); return undefined; }
    const t = setTimeout(() => setCoupureVisible(true), 6000);
    return () => clearTimeout(t);
  }, [connected]);
  const {
    announce,
    announceTicket,
    announceWaitingMessage,
    clearQueue,
    speaking,
    available,
    voices,
  } = voiceAnnouncer;

  // Ref STABLE vers l'annonceur. L'effet socket NE DOIT PAS dépendre de l'identité de
  // `voiceAnnouncer` : celle-ci change à chaque `setSpeaking(true)` (au démarrage d'une
  // annonce), ce qui re-monterait l'effet, dont le nettoyage appelait `cancel()` → la voix
  // se coupait elle-même en plein appel (« err: interrupted », voix « 1 fois sur 2 »).
  const voiceAnnouncerRef = useRef(voiceAnnouncer);
  voiceAnnouncerRef.current = voiceAnnouncer;

  // Remonte au serveur les voix TTS installées sur CE poste d'affichage. Ainsi la console
  // (parfois sur Ubuntu distant) et la page /sound proposent les VRAIES voix du serveur,
  // pas celles du navigateur qui configure. Réémis quand la liste change (chargée en asynchrone).
  useEffect(() => {
    if (!socket || !connected || !voices || voices.length === 0) return;
    socket.emit('reportVoices', {
      voices: voices.map((v) => ({ name: v.name, lang: v.lang, voiceURI: v.voiceURI, localService: v.localService, default: v.default })),
    });
  }, [socket, connected, voices]);

  // Test vocal déclenché À DISTANCE (bouton « Tester » de /sound ou de la console) : on parle
  // ICI, sur le poste d'affichage → on l'entend sur le serveur (là où sont les haut-parleurs).
  // Résout la voix demandée contre les voix locales (repli : 1re voix de la langue → défaut).
  useEffect(() => {
    if (!socket) return undefined;
    const onTest = (p) => {
      if (p?.kind === 'beep') {
        const el = document.getElementById('call-sound');
        if (el) { el.currentTime = 0; el.play().catch(() => {}); }
        return;
      }
      if (!('speechSynthesis' in window)) return;
      const u = new SpeechSynthesisUtterance(p?.text || 'Test');
      u.lang = p?.locale || 'fr-FR';
      const two = (p?.locale || 'fr').slice(0, 2).toLowerCase();
      const v = (p?.voiceURI && voices.find((x) => x.voiceURI === p.voiceURI))
        || voices.find((x) => (x.lang || '').toLowerCase().startsWith(two));
      if (v) u.voice = v;
      u.rate = Number(p?.rate) || 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    };
    socket.on('testAnnounce', onTest);
    return () => socket.off('testAnnounce', onTest);
  }, [socket, voices]);

  // Ref pour garder la config advertising à jour sans re-render le useEffect socket
  const advertisingRef = useRef(config?.advertising);
  // Sérialiser pour éviter les re-runs inutiles quand la référence objet change sans changement de contenu
  const advertisingKey = JSON.stringify({
    enabled: config?.advertising?.enabled,
    idleTimeout: config?.advertising?.idleTimeout
  });

  useEffect(() => {
    advertisingRef.current = config?.advertising;
    const adEnabled = config?.advertising?.enabled;
    const timeout = (config?.advertising?.idleTimeout || 5) * 60 * 1000;

    // Nettoyer tout timer existant avant de (re)démarrer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (adEnabled) {
      console.log(`[Pub] Timer idle démarré: ${timeout / 1000}s`);
      idleTimerRef.current = setTimeout(() => {
        console.log('[Pub] Idle timeout atteint → mode pub');
        setIsIdle(true);
      }, timeout);
    } else {
      setIsIdle(false);
    }

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [advertisingKey]);

  // Fonction pour réinitialiser le timer d'inactivité (mode pub)
  // Utilise advertisingRef pour ne pas dépendre de config (évite boucle useEffect)
  const resetIdleTimer = useCallback(() => {
    setIsIdle(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    const adConfig = advertisingRef.current;
    const timeout = (adConfig?.idleTimeout || 5) * 60 * 1000;
    console.log(`[Pub] resetIdleTimer — enabled=${adConfig?.enabled}, timeout=${timeout/1000}s`);
    if (adConfig?.enabled) {
      idleTimerRef.current = setTimeout(() => {
        console.log('[Pub] Idle timeout atteint → mode pub');
        setIsIdle(true);
      }, timeout);
    }
  }, []); // pas de deps — utilise la ref

  // Fonction pour mettre à jour les données d'affichage
  const updateDisplayData = useCallback((data) => {
    if (!data) {
      console.log('Données de mise à jour nulles ou non définies');
      return;
    }
    
    console.log('Mise à jour des données d\'affichage reçue');
    
    if (data.activeTickets) {
      // Filtre de services poussé par la console (master/slave). [] = tous les services.
      const activeTickets = displayServicesRef.current.length
        ? data.activeTickets.filter(t => displayServicesRef.current.includes(t.serviceType))
        : data.activeTickets;
      // Filtrer les tickets en service
      const serving = activeTickets.filter(ticket => ticket.status === 'serving');
      setServingTickets(serving);
      console.log(`Tickets en service mis à jour: ${serving.length}`);

      // Compter les tickets en attente par service
      const waiting = activeTickets.filter(ticket => ticket.status === 'waiting');
      const counts = {};
      waiting.forEach(ticket => {
        const type = ticket.serviceType;
        counts[type] = (counts[type] || 0) + 1;
      });
      
      console.log(`Tickets en attente: ${waiting.length}`);
      setWaitingCounts(counts);
    } else {
      console.log('Pas de tickets actifs dans les données reçues');
    }
    
    setLastUpdateTime(new Date());
  }, []);

  // Repli REST quand la socket décroche. On interroge `/api/display/state`, qui renvoie la
  // MÊME charge utile que l'événement socket `displayUpdate` (tickets `waiting` + `serving`).
  // Avant : `/api/tickets/active` — qui ne renvoie QUE les tickets en service. Aucun ticket
  // `waiting` n'arrivait ici, donc `updateDisplayData` comptait 0 partout : les carrés
  // « en attente » retombaient à zéro alors que des clients patientaient.
  const fetchTicketsManually = useCallback(async () => {
    try {
      console.log('Tentative de récupération manuelle de l\'état d\'affichage via API REST');
      const response = await axios.get('/api/display/state');

      if (response.data.success) {
        const payload = response.data.data;
        console.log(`${payload.activeTickets?.length ?? 0} tickets récupérés via API REST`);

        // Mettre à jour avec les mêmes fonctions que pour les données socket
        updateDisplayData({ ...payload, message: 'récupération manuelle via API' });
      } else {
        console.log('Échec de la récupération de l\'état d\'affichage via API REST');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération manuelle:', error);
    }
  }, [updateDisplayData]);

  // Demander manuellement une mise à jour des données
  const requestUpdate = useCallback(() => {
    if (socket && connected) {
      console.log('Demande de mise à jour des données via Socket.IO');
      socket.emit('requestDisplayUpdate');
    } else {
      console.log('Socket non connecté, tentative de récupération via API REST');
      fetchTicketsManually();
    }
  }, [socket, connected, fetchTicketsManually]);

  // Chargement des voix disponibles pour la synthèse vocale
  useEffect(() => {
    const loadVoices = () => {
      if (!window.speechSynthesis) return;
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setAvailableVoices(voices);
        // Sélectionner une voix par défaut selon la langue choisie
        const langVoices = voices.filter(voice => voice.lang.startsWith(language.split('-')[0]));
        if (langVoices.length > 0) {
          setSelectedVoice(langVoices[0].name);
        } else {
          setSelectedVoice(voices[0].name);
        }
      }
    };

    // L'API de synthèse vocale peut prendre du temps à charger les voix
    if (window.speechSynthesis) {
      // Essayer d'obtenir les voix immédiatement
      loadVoices();
      
      // Écouter l'événement voiceschanged
      window.speechSynthesis.onvoiceschanged = loadVoices;
      
      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, [language]);

  // Fonction pour déverrouiller l'audio (nécessaire pour la politique autoplay)
  /**
   * Déverrouille l'audio SANS dépendre d'un fichier.
   *
   * L'ancienne sonde jouait `ding.mp3`. Si ce fichier ne charge pas — absent, format refusé
   * par la WebView de la TV — la promesse est rejetée et l'écran reste marqué « verrouillé »
   * ALORS MÊME que le navigateur autorise le son : l'APK pose déjà
   * `mediaPlaybackRequiresUserGesture = false`. Le bouton restait donc affiché en permanence
   * devant les clients, et l'exploitant croyait le son bloqué.
   *
   * On sonde donc d'abord par l'API audio du navigateur, qui n'a besoin d'aucun fichier :
   * reprendre un contexte suffit à savoir si le son est permis. Le fichier n'est plus qu'une
   * confirmation secondaire.
   */
  const unlockAudio = useCallback(async () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      try {
        const ctx = new Ctx();
        if (ctx.state === 'suspended') await ctx.resume();
        if (ctx.state === 'running') {
          setAudioUnlocked(true);
          try { await ctx.close(); } catch { /* déjà fermé */ }
          return true;
        }
        try { await ctx.close(); } catch { /* déjà fermé */ }
      } catch { /* API indisponible : on tente le fichier ci-dessous */ }
    }
    const audioElement = document.getElementById('call-sound');
    if (!audioElement) return false;
    try {
      await audioElement.play();
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioUnlocked(true);
      return true;
    } catch {
      return false; // le navigateur exige un geste : la bannière prend le relais
    }
  }, []);

  // Déverrouillage audio au PREMIER geste utilisateur (n'importe où), pas seulement sur
  // un bouton précis — sinon la politique autoplay du navigateur laisse le son inaudible.
  useEffect(() => {
    if (audioUnlocked) return;
    // Sonde au montage : dans la fenêtre Electron du serveur (autoplayPolicy
    // 'no-user-gesture-required') le `play()` réussit sans geste → on est déverrouillé
    // immédiatement et la bannière ne s'affiche pas. Dans Chrome sans geste, il échoue :
    // la bannière et l'écoute du premier clic/touche ci-dessous prennent le relais.
    unlockAudio();
    const unlock = () => {
      unlockAudio();
      // Débloquer la SYNTHÈSE vocale exige un `speak()` DANS le geste : `resume()` seul ne
      // satisfait pas l'activation utilisateur de Chrome/Firefox. On amorce donc avec un énoncé
      // silencieux — ensuite les annonces déclenchées par socket (sans geste) fonctionnent.
      try {
        if (window.speechSynthesis) {
          window.speechSynthesis.resume();
          const primer = new SpeechSynthesisUtterance('​'); // caractère invisible
          primer.volume = 0;
          window.speechSynthesis.speak(primer);
        }
      } catch (e) { /* ignore */ }
    };
    const opts = { once: true, passive: true };
    document.addEventListener('click', unlock, opts);
    document.addEventListener('keydown', unlock, opts);
    document.addEventListener('touchstart', unlock, opts);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, [audioUnlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Joue le bip `count` fois PUIS exécute `cb` (l'annonce vocale). Séquentiel : la voix
  // ne démarre qu'après le DERNIER bip (sinon le bip la masque). Nombre de bips et on/off
  // réglables dans la config (onglet Apparence → display.beepCount / display.beepEnabled).
  const playBeepsThen = (audioId, count, cb) => {
    const el = document.getElementById(audioId);
    let done = false;
    const finish = () => { if (!done) { done = true; cb(); } };
    let remaining = Math.max(0, Number(count) || 0);
    if (!el || remaining === 0) { finish(); return; }
    const onEnded = () => {
      remaining -= 1;
      if (remaining > 0) { el.currentTime = 0; el.play().catch(finish); }
      else { el.removeEventListener('ended', onEnded); finish(); }
    };
    el.addEventListener('ended', onEnded);
    el.currentTime = 0;
    el.play().catch(finish);
    // repli si l'événement 'ended' ne se déclenche pas (durée ~ bips + marge)
    setTimeout(() => { el.removeEventListener('ended', onEnded); finish(); }, 1800 * (Number(count) || 1) + 2500);
  };

  // Mettre à jour la date/heure chaque minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Enregistrer comme client "display" au montage et configurer les écouteurs d'événements
  useEffect(() => {
    if (socket && connected) {
      console.log('Socket connecté, enregistrement comme écran d\'affichage');
      // Identité stable de l'écran (§5.bis) : un displayId persistant → le registre
      // le suit d'une session à l'autre ; la console le cible par cet ID, pas par IP.
      const rand = () => 'disp-' + Math.random().toString(36).slice(2, 10);
      let identity;
      try {
        let id = localStorage.getItem('ticketflow.displayId');
        if (!id) { id = rand(); localStorage.setItem('ticketflow.displayId', id); }
        identity = { displayId: id, name: localStorage.getItem('ticketflow.displayName') || undefined };
      } catch { identity = { displayId: rand() }; }
      setMyDisplayId(identity.displayId);
      registerClient('display', identity);

      // Config poussée par la console : mode master/slave + filtre de services.
      socket.on('displayConfig', (cfg) => {
        console.log('displayConfig reçu:', cfg);
        displayServicesRef.current = Array.isArray(cfg?.services) ? cfg.services : [];
        displaySoundRef.current = cfg?.sound === true;
        setDisplayEnabled(cfg?.enabled !== false); // non approuvé → écran « en attente »
        setQuotaEcrans(cfg?.quota || null);         // pour DIRE pourquoi l'écran attend
        requestUpdate(); // ré-applique le filtre immédiatement
      });

      // Récupérer les données initiales
      socket.emit('requestInitialData');
      console.log('Demande des données initiales envoyée');

      // Rafraîchissement debouncé (250 ms) des compteurs sur événement « push ». La queue
      // émet newTicket/ticketCalled/ticketCompleted (le ticket seul, sans la liste complète) ;
      // on redemande alors un displayUpdate complet. Le debounce coalesce les rafales tout en
      // restant quasi-instantané — remplace les anciens délais (10 s / 60 s) qui n'étaient
      // masqués que par la boucle de poll supprimée avec la mémoïsation du hook voix.
      const scheduleDisplayRefresh = () => {
        if (updateTimeoutRef.current) return;
        updateTimeoutRef.current = setTimeout(() => {
          updateTimeoutRef.current = null;
          requestUpdate();
        }, 250);
      };

      // Le timer d'inactivité (mode pub) est géré par le useEffect [config.advertising]

      socket.on('initialData', (data) => {
        console.log('Données initiales reçues');
        updateDisplayData(data);
      });

      socket.on('displayUpdate', (data) => {
        console.log('Mise à jour d\'affichage reçue');
        updateDisplayData(data);
        // NE PAS reset le timer idle ici — c'est un refresh périodique, pas une activité ticket
      });

      socket.on('ticketCalled', (data) => {
        console.log(`Ticket appelé: ${data.ticket?.ticketId} au guichet ${data.counterId}`);
        resetIdleTimer();

        // Vérifier que le ticket et counterId existent
        if (!data.ticket || !data.counterId) {
          console.error("Données d'appel de ticket incomplètes:", data);
          return;
        }

        // Afficher la modale pour l'appel
        setRecallModalData({
          ticket: data.ticket,
          counterId: data.counterId,
          counterName: data.counterName || `Guichet ${data.counterId}`,
          isRecall: false
        });

        // Mettre à jour les états pour afficher l'animation et l'alerte
        setLastCalled(data);
        setShowCallAnimation(true);

        setCallAlert({
          ticket: data.ticket,
          counterId: data.counterId,
          counterName: data.counterName || `Guichet ${data.counterId}`
        });

        console.log("Alerte définie:", {
          ticketId: data.ticket.ticketId,
          counterId: data.counterId,
          counterName: data.counterName || `Guichet ${data.counterId}`
        });
        
        // SÉQUENCE bip → voix : l'annonce ne part qu'APRÈS le bip, sinon le bip masque
        // le début de la voix (« ça se chevauche »). Garde anti-double-annonce.
        let announced = false;
        const announce = () => {
          if (announced) return; announced = true;
          // Verrou `available` volontairement RETIRÉ (proxy peu fiable : dépend de getVoices(),
          // vide sur certains moteurs Android alors que la synthèse marche). Le hook se protège
          // via hasSpeech(). On garde le mute PAR ÉCRAN (master/slave) et l'accessibilité.
          if (voiceEnabled && displaySoundRef.current) {
            voiceAnnouncerRef.current.announceTicketMultilang(data.ticket, data.counterId);
          }
        };
        const beepOn = soundEnabled && config?.display?.beepEnabled !== false && displaySoundRef.current;
        const beepCount = Number(config?.display?.beepCount) || 1;
        if (beepOn) playBeepsThen('call-sound', beepCount, announce);
        else announce();
        
        // Cacher l'animation après quelques secondes
        const duration = config?.display?.ticketCallingDuration || 10;
        console.log(`Animation d'appel configurée pour disparaître après ${duration} secondes`);

        setTimeout(() => {
          setShowCallAnimation(false);
          setCallAlert(null);
          // La modale se ferme toute seule via son propre useEffect
        }, duration * 1000);
        
        // Rafraîchir les compteurs QUASI-IMMÉDIATEMENT après un appel. Le debounce court
        // (250 ms) coalesce les rafales sans le délai de 60 s d'origine — ce délai n'était
        // masqué que par l'ancienne boucle de poll (supprimée avec la mémoïsation du hook voix).
        scheduleDisplayRefresh();

      });

      socket.on('newTicket', (ticket) => {
        console.log(`Nouveau ticket créé: ${ticket.ticketId} pour le service ${ticket.serviceType}`);
        // Maj quasi-immédiate à la création (avant : +10 s → ticket imprimé lent à apparaître).
        scheduleDisplayRefresh();
      });

      // Ticket terminé au guichet : la queue émet 'ticketCompleted' — que /display n'écoutait
      // PAS → la file ne se vidait qu'au bout de 60 s. On rafraîchit tout de suite.
      socket.on('ticketCompleted', () => {
        scheduleDisplayRefresh();
      });

      // Ticket ANNULÉ — même angle mort que ci-dessus. C'est le seul événement émis par
      // « Vider la file d'attente » (un par ticket annulé) : sans cet écouteur, l'écran
      // restait figé sur une file déjà vidée jusqu'au poll de 60 s. Le rafraîchissement
      // est débouncé (250 ms), donc un vidage de N tickets ne déclenche qu'un seul appel.
      socket.on('ticketCancelled', () => {
        scheduleDisplayRefresh();
      });

      // Nouveaux événements pour RAPPEL et PAUSE
      socket.on('ticketRecalled', (data) => {
        console.log(`Ticket rappelé:`, data);
        resetIdleTimer();

        // Vérifier que les données nécessaires existent
        if (!data.ticket || !data.counterId) {
          console.error("Données de rappel de ticket incomplètes:", data);
          return;
        }

        // Afficher la modale de rappel
        setRecallModalData({
          ticket: data.ticket,
          counterId: data.counterId,
          counterName: data.counterName || `Guichet ${data.counterId}`,
          isRecall: true
        });

        // Afficher une animation de rappel
        setLastCalled({
          ticket: data.ticket,
          counterId: data.counterId,
          type: 'recall'
        });

        setShowCallAnimation(true);

        // SÉQUENCE bip → voix (rappel) : l'annonce ne part qu'après le bip.
        let announcedR = false;
        const announceR = () => {
          if (announcedR) return; announcedR = true;
          // Gate `available` retiré (cf. appel) — le hook se protège via hasSpeech().
          if (voiceEnabled && displaySoundRef.current) {
            voiceAnnouncerRef.current.announceRecallMultilang(data.ticket, data.counterId);
          }
        };
        const beepOnR = soundEnabled && config?.display?.beepEnabled !== false && displaySoundRef.current;
        const beepCountR = Number(config?.display?.beepCount) || 1;
        if (beepOnR) playBeepsThen('notification-sound', beepCountR, announceR);
        else announceR();

        // Cacher l'animation après quelques secondes
        setTimeout(() => {
          setShowCallAnimation(false);
          setCallAlert(null);
        }, 5000);
      });

      socket.on('ticketServicePaused', (data) => {
        console.log(`Service en pause: ticket ${data.ticketId} au guichet ${data.counterId}`);

        // Mettre à jour l'affichage pour montrer la pause
        setServingTickets(prev => prev.map(ticket =>
          ticket.ticketId === data.ticketId
            ? { ...ticket, status: 'serving_paused' }
            : ticket
        ));

        // Demander une mise à jour
        requestUpdate();
      });

      socket.on('ticketServiceResumed', (data) => {
        console.log(`Service repris: ticket ${data.ticketId} au guichet ${data.counterId}`);

        // Mettre à jour l'affichage pour montrer la reprise
        setServingTickets(prev => prev.map(ticket =>
          ticket.ticketId === data.ticketId
            ? { ...ticket, status: 'serving' }
            : ticket
        ));

        // Demander une mise à jour
        requestUpdate();
      });
    } else if (!connected && !loading && config) {
      // Récupérer les données manuellement si le Socket n'est pas connecté
      console.log('Socket non connecté, récupération des données via API REST');
      fetchTicketsManually();
    }
    // diminuer la vitesse de rafrechissement du screen
    // Demander périodiquement des mises à jour
    const updateInterval = setInterval(() => {
      console.log('Mise à jour périodique programmée');
      if (socket && connected) {
        requestUpdate();
      }
    }, 60000); // Toutes les 60 secondes
    
    return () => {
      // NE PAS couper la voix ici : ce nettoyage se déclenche aussi sur reconnexion socket,
      // et un `cancel()` interromprait une annonce en cours (« err: interrupted »). La file
      // se vide d'elle-même quand chaque énoncé se termine ; au vrai démontage de page, le
      // navigateur arrête la synthèse.

      if (socket) {
        console.log("Nettoyage des écouteurs d 'événements Socket.IO");
        socket.off('initialData');
        socket.off('displayUpdate');
        socket.off('displayConfig');
        socket.off('ticketCalled');
        socket.off('newTicket');
        socket.off('ticketCompleted');
        socket.off('ticketCancelled');
        socket.off('ticketRecalled');
        socket.off('ticketServicePaused');
        socket.off('ticketServiceResumed');
      }
      clearInterval(updateInterval);
      // NE PAS clear idleTimerRef ici — il est géré par le useEffect [config.advertising]
    };
    // `voiceAnnouncer` VOLONTAIREMENT ABSENT des deps : on passe par `voiceAnnouncerRef`.
    // Sinon l'effet se re-monte à chaque `setSpeaking` et coupe la voix (cf. ref ci-dessus).
  }, [socket, connected, config, loading, registerClient, updateDisplayData, requestUpdate, fetchTicketsManually, soundEnabled, voiceEnabled]);

  // Si les données sont en cours de chargement
  // Écran NON approuvé (facturation) : ne montre pas la file, invite à faire approuver.
  if (!displayEnabled) {
    // Deux causes, deux gestes : soit la licence est pleine (il faut en ajouter une), soit
    // l'écran attend un accord. Un message unique laissait l'exploitant appeler pour rien.
    const quotaPlein = quotaEcrans && quotaEcrans.quota !== null && quotaEcrans.restants === 0;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center p-8 max-w-lg">
          <div className="text-6xl mb-4">{quotaPlein ? '🖥️🔒' : '🖥️⏳'}</div>
          <h1 className="text-3xl font-bold mb-2">
            {quotaPlein ? 'Licence : tous les écrans sont utilisés' : "En attente d'approbation"}
          </h1>
          <p className="text-gray-300 mb-6">
            {quotaPlein
              ? `Votre licence inclut ${quotaEcrans.quota} écran${quotaEcrans.quota > 1 ? 's' : ''}, tous déjà en service. Contactez votre fournisseur pour en ajouter un.`
              : "Cet écran doit être approuvé par l'administrateur avant d'afficher la file d'attente."}
          </p>
          <p className="text-sm text-gray-400">Identifiant à communiquer :</p>
          <p className="font-mono text-lg bg-gray-800 inline-block px-4 py-2 rounded mt-1 select-all">{myDisplayId || '…'}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-600">
        <div className="p-8 bg-white rounded-lg shadow-md text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-700">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si une erreur s'est produite
  if (error || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-600">
        <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold mb-4">Erreur</h1>
          <p className="mb-6 text-gray-700">{error || "La configuration n'a pas pu être chargée"}</p>
          <button 
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Format de l'heure: HH:MM
  const timeString = currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  // Format de la date: jj/mm/aaaa
  const dateString = currentDate.toLocaleDateString('fr-FR', { 
    day: '2-digit', 
    month: '2-digit',
    year: 'numeric' 
  });

  // Facteur d'échelle de l'affichage (réglable dans l'onglet Styles, stocké dans le seed).
  // On agrandit la boîte de 1/scale puis on la zoome de `scale` → contenu mis à l'échelle
  // MAIS l'affichage remplit toujours exactement l'écran (fit-to-screen sans zoom navigateur).
  const displayScale = Number(config?.styles?.display?.scale) || 1;
  // Disposition de l'affichage (config, pilotée console) : 'grid' = grille des services
  // (compteurs d'attente) plein écran ; sinon disposition classique (2 panneaux).
  const isGridLayout = config?.display?.layout === 'grid';

  // Libellés réglables depuis la console. `langues` = mêmes langues et même ordre que les noms
  // de service ; `languePrincipale` = la première, celle qui sert là où une seule valeur a du
  // sens — un numéro de ticket ne s'écrit pas trois fois.
  const langues = orderedServiceLangs(config.styles?.display, 'x');
  const languePrincipale = langues[0]?.key || 'fr';
  // Le CODE DE TICKET a son propre réglage : c'est ce que le client doit reconnaître sur le
  // papier, à l'écran et à l'oreille. L'ordre des noms, lui, ne règle qu'une mise en page.
  const langueTicket = langueCodeTicket(config, languePrincipale);
  return (
    <div
      className="display-container"
      style={displayScale !== 1 ? {
        zoom: displayScale,
        height: `calc(100vh / ${displayScale})`,
        width: `calc(100vw / ${displayScale})`,
      } : undefined}
    >
      {/* Sons pour les notifications */}
      <audio id="call-sound" src="/sounds/ding.mp3" preload="auto"></audio>
      <audio id="notification-sound" src="/sounds/notification.mp3" preload="auto"></audio>
      <audio id="next-sound" src="/sounds/next.mp3" preload="auto"></audio>
      
      {/* En-tête */}
      <header className="display-header">
        <div className="display-logo-container">
          <img 
            src={config.agency?.logo || '/logo.png'} 
            alt={config.agency?.name || 'Logo'} 
            className="display-logo"
            onError={(e) => {
              e.target.src = '/logo.png'; // Fallback si l'image ne charge pas
              e.target.onerror = null;
            }}
          />
          <div className="display-agency-info">
            <h1>{config.agency?.name || 'Agence'}</h1>
            <p>{config.agency?.company || 'Entreprise'}</p>
          </div>
        </div>
        <div className="display-datetime">
          <div className="display-time">{timeString}</div>
          <div className="display-date">{dateString}</div>
          {coupureVisible && (
            // « Reconnexion… » et non « Non connecté » : depuis que les tentatives sont
            // ILLIMITÉES, l'écran revient tout seul — le dire évite d'envoyer quelqu'un
            // redémarrer une machine qui n'a rien. Affiché seulement après quelques
            // secondes : une micro-coupure ne doit pas faire clignoter un bandeau rouge
            // devant la salle d'attente.
            <div className="text-xs text-amber-300 mt-1">Reconnexion…</div>
          )}
        </div>
      </header>
      
      {/* Contenu principal — mode pub si inactif, sinon affichage normal */}
      {/* La publicité est un CONFORT ; la file d'attente est le MÉTIER : une slide fautive
          doit coûter la publicité, jamais l'écran de la salle d'attente. D'où le garde-fou. */}
      {isIdle && config?.advertising?.enabled ? (
        <AdErrorBoundary resetKey={config?.advertising?.slides?.length}>
          <AdSlideshow config={config} serverUrl="" onInterrupt={resetIdleTimer} />
        </AdErrorBoundary>
      ) : (
      <main className="display-main">
        {/* Mode GRILLE (config.display.layout === 'grid') : grille des services plein écran. */}
        {isGridLayout && <ServiceGrid config={config} waitingCounts={waitingCounts} />}

        {/* Panneau de gauche: Message de bienvenue (masqué en mode grille) */}
        <div className="display-welcome-panel" style={isGridLayout ? { display: 'none' } : undefined}>
          <div className="welcome-message-ar rtl" dir="rtl">{config.ui?.welcomeMessage?.ar}</div>
          <div className="welcome-message-fr">{config.ui?.welcomeMessage?.fr}</div>

          {/* Afficher le nombre de personnes en attente si activé */}
          {config.display?.showQueueLength && (
            <div className="service-counts-grid">
              {config.serviceTypes?.map(service => (
                <div key={service.id} className="service-count-item">
                  {/* Ordre / visibilité / taille des langues : pilotés depuis la console (Styles). */}
                  {orderedServiceLangs(config.styles?.display, 'service-name').map((l) => {
                    const t = l.text(service);
                    return t ? <div key={l.key} className={l.cls} dir={l.dir}>{t}</div> : null;
                  })}
                  <div className="service-count">
                    {waitingCounts[service.id] || 0}
                  </div>
                  {langues.map((l) => (
                    <div key={l.key} className="waiting-text" dir={l.dir}>
                      {libelleEcran(config, 'waiting', l.key)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          
          {/* Actualisation manuelle — MASQUÉE par défaut. C'est une commande de diagnostic,
              et cet écran fait face au public : elle n'aide personne dans la salle d'attente,
              et n'importe qui peut la toucher. L'exploitant l'affiche depuis la console quand
              il en a besoin (onglet Affichage). Les données arrivent de toute façon par socket
              et par le relevé périodique — ce bouton ne conditionne rien. */}
          {config?.display?.showRefreshButton === true && (
            <button
              className="mt-8 px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition"
              onClick={requestUpdate}
              title="Forcer une actualisation"
            >
              🔄
            </button>
          )}
        </div>

        {/* Panneau de droite: Tickets en service (masqué en mode grille) */}
        <div className="display-tickets-panel" style={isGridLayout ? { display: 'none' } : undefined}>
          <div className="tickets-header" dir={languePrincipale === 'ar' ? 'rtl' : undefined}>
            {libelleEcran(config, 'ticketsHeader', languePrincipale)}
          </div>
          
          <div className="tickets-list">
            {servingTickets.map((ticket) => {
              const service = config.serviceTypes.find(s => s.id === ticket.serviceType);
              const counter = config.counters.find(c => c.id === ticket.counterId);
              
              return (
                <div
                  key={ticket.ticketId}
                  className={`ticket-item ${ticket.status === 'serving_paused' ? 'paused' : ''}`}
                >
                  <div className="ticket-number">
                    {/* Code de service PAR LANGUE : `ticketId` reste la clé technique, ce qui
                        s'affiche est recomposé (voir utils/serviceCode). */}
                    {ticket.label || ticketLabel(config, ticket, langueTicket)}
                    {ticket.status === 'serving_paused' && (
                      <div className="pause-indicator" style={{
                        fontSize: '12px',
                        color: '#ef4444',
                        fontWeight: 'bold',
                        marginTop: '4px'
                      }}>
                        PAUSE
                      </div>
                    )}
                  </div>
                  <div
                    className="counter-badge"
                    style={{
                      backgroundColor: ticket.status === 'serving_paused'
                        ? '#ef4444'
                        : (service?.color || 'var(--counter-bg-color)')
                    }}
                  >
                    {counter?.id || '?'}
                  </div>
                </div>
              );
            })}
            
            {/* Si aucun ticket en service */}
            {servingTickets.length === 0 && (
              <div
                className="text-center py-12 text-white opacity-75"
                dir={languePrincipale === 'ar' ? 'rtl' : undefined}
              >
                {libelleEcran(config, 'noTicket', languePrincipale)}
              </div>
            )}
          </div>
        </div>
      </main>
      )}

      {/* Popup d'appel/rappel — UNIQUE notification, entièrement configurable depuis la
          console admin (config.display.recall : couleurs, taille, durée, disposition).
          Sert appel ET rappel via `isRecall`. Monté au niveau racine → visible quel que soit
          le mode (classique, grille, pub). Les anciens popups blancs (AlertNotification +
          bannière inline) ont été supprimés (doublons visuels + double annonce vocale). */}
      {recallModalData && (
        <RecallModal
          config={config}
          lang={langueTicket}
          ticket={recallModalData.ticket}
          counterId={recallModalData.counterId}
          counterName={recallModalData.counterName}
          isRecall={recallModalData.isRecall}
          onClose={closeRecallModal}
          settings={config?.display?.recall || {}}
          duration={(config?.display?.ticketCallingDuration || 5) * 1000}
        />
      )}

      {/* Pied de page */}
      <footer className="display-footer">
        {config.agency?.companySlogan || ''}
        {/* Bandeau pub central (ads.amia.fr) — best-effort, isolé, désactivé si non configuré. */}
        <AdBanner ads={config?.ads} placement="scene:bandeau" />
        
        {/* Déblocage autoplay : sur un poste d'affichage, le navigateur exige un geste
            utilisateur avant de jouer le son. Ce bouton ne s'affiche qu'AVANT ce déblocage.
            Tous les AUTRES réglages son/voix ont été déplacés vers la page dédiée /sound
            (accessible depuis /manage), pilotée par la config serveur. */}
        {!audioUnlocked && config?.display?.showSoundButton !== false && (
          <div className="absolute bottom-4 right-4">
            <button
              onClick={unlockAudio}
              className="p-3 rounded-lg bg-red-500 text-white font-bold animate-pulse"
              title="Cliquez pour activer le son"
            >
              🔓 Activer le son
            </button>
          </div>
        )}
      </footer>
      {/* (Bannière inline « Ticket appelé » supprimée : doublon du popup RecallModal.) */}

     

   
    
    </div>
  );
};

export default DisplayScreen;
// // // import React, { useState, useEffect, useCallback, useRef } from 'react';
// // // import { useConfig } from '../../contexts/ConfigContext';
// // // import { useSocket } from '../../contexts/SocketContext';
// // // import axios from 'axios';

// // // /**
// // //  * Écran d'affichage pour la salle d'attente
// // //  * Affiche les tickets appelés et les guichets correspondants avec débogage amélioré
// // //  */
// // // const DisplayScreen = () => {
// // //   const { config, loading, error } = useConfig();
// // //   const { socket, connected, registerClient } = useSocket();
  
// // //   // États
// // //   const [currentDate, setCurrentDate] = useState(new Date());
// // //   const [servingTickets, setServingTickets] = useState([]);
// // //   const [waitingCounts, setWaitingCounts] = useState({});
// // //   const [lastCalled, setLastCalled] = useState(null);
// // //   const [showCallAnimation, setShowCallAnimation] = useState(false);
// // //   const [lastUpdateTime, setLastUpdateTime] = useState(null);
// // //   const [debugMode, setDebugMode] = useState(false);
// // //   const [debugInfo, setDebugInfo] = useState([]);
  
// // //   // Référence pour les tickets actuels
// // //   const activeTicketsRef = useRef([]);
  
// // //   // Référence pour le timer de mode débogage
// // //   const debugTimerRef = useRef(null);

// // //   // Fonction pour ajouter un message de débogage
// // //   const addDebugMessage = useCallback((message) => {
// // //     setDebugInfo(prev => {
// // //       const newInfo = [{
// // //         time: new Date().toLocaleTimeString(),
// // //         message
// // //       }, ...prev];
      
// // //       // Limiter à 20 messages maximum
// // //       if (newInfo.length > 20) {
// // //         return newInfo.slice(0, 20);
// // //       }
// // //       return newInfo;
// // //     });
// // //     console.log(`[DEBUG] ${message}`);
// // //   }, []);

// // //   // Mettre à jour la date/heure chaque minute
// // //   useEffect(() => {
// // //     const timer = setInterval(() => {
// // //       setCurrentDate(new Date());
// // //     }, 60000);
    
// // //     return () => clearInterval(timer);
// // //   }, []);

// // //   // Fonction pour mettre à jour les données d'affichage
// // //   const updateDisplayData = useCallback((data) => {
// // //     if (!data) {
// // //       addDebugMessage('Données de mise à jour nulles ou non définies');
// // //       return;
// // //     }
    
// // //     addDebugMessage(`Mise à jour des données d'affichage reçue: ${JSON.stringify(data.message || 'mise à jour standard')}`);
    
// // //     if (data.activeTickets) {
// // //       // Garder une copie pour le débogage
// // //       activeTicketsRef.current = data.activeTickets;
      
// // //       // Filtrer les tickets en service
// // //       const serving = data.activeTickets.filter(ticket => ticket.status === 'serving');
// // //       setServingTickets(serving);
// // //       addDebugMessage(`Tickets en service mis à jour: ${serving.length}`);
      
// // //       // Compter les tickets en attente par service
// // //       const waiting = data.activeTickets.filter(ticket => ticket.status === 'waiting');
// // //       const counts = {};
// // //       waiting.forEach(ticket => {
// // //         const type = ticket.serviceType;
// // //         counts[type] = (counts[type] || 0) + 1;
// // //       });
      
// // //       addDebugMessage(`Tickets en attente: ${waiting.length} (${JSON.stringify(counts)})`);
// // //       setWaitingCounts(counts);
// // //     } else {
// // //       addDebugMessage('Pas de tickets actifs dans les données reçues');
// // //     }
    
// // //     setLastUpdateTime(new Date());
// // //   }, [addDebugMessage]);

// // //   // Récupérer manuellement les tickets via l'API REST comme solution de secours
// // //   const fetchTicketsManually = useCallback(async () => {
// // //     try {
// // //       addDebugMessage('Tentative de récupération manuelle des tickets via API REST');
// // //       const response = await axios.get('/api/tickets/active');
      
// // //       if (response.data.success) {
// // //         const tickets = response.data.data;
// // //         addDebugMessage(`${tickets.length} tickets récupérés via API REST`);
        
// // //         // Mettre à jour avec les mêmes fonctions que pour les données socket
// // //         updateDisplayData({ activeTickets: tickets, message: 'récupération manuelle via API' });
// // //       } else {
// // //         addDebugMessage('Échec de la récupération des tickets via API REST');
// // //       }
// // //     } catch (error) {
// // //       addDebugMessage(`Erreur lors de la récupération manuelle: ${error.message}`);
// // //     }
// // //   }, [addDebugMessage, updateDisplayData]);

// // //   // Demander manuellement une mise à jour des données
// // //   const requestUpdate = useCallback(() => {
// // //     if (socket && connected) {
// // //       addDebugMessage('Demande de mise à jour des données via Socket.IO');
// // //       socket.emit('requestDisplayUpdate');
// // //     } else {
// // //       addDebugMessage('Socket non connecté, tentative de récupération via API REST');
// // //       fetchTicketsManually();
// // //     }
// // //   }, [socket, connected, addDebugMessage, fetchTicketsManually]);

// // //   // Enregistrer comme client "display" au montage et configurer les écouteurs d'événements
// // //   useEffect(() => {
// // //     // Configurer les écouteurs pour les événements Socket.IO
// // //     const setupSocketListeners = () => {
// // //       if (!socket) return;
      
// // //       addDebugMessage('Configuration des écouteurs d\'événements Socket.IO');
      
// // //       // Récupérer les données initiales
// // //       socket.emit('requestInitialData');
// // //       addDebugMessage('Demande des données initiales envoyée');
      
// // //       socket.on('initialData', (data) => {
// // //         addDebugMessage('Données initiales reçues');
// // //         updateDisplayData(data);
// // //       });
      
// // //       socket.on('displayUpdate', (data) => {
// // //         addDebugMessage('Mise à jour d\'affichage reçue');
// // //         updateDisplayData(data);
// // //       });
      
// // //       socket.on('ticketCalled', (data) => {
// // //         addDebugMessage(`Ticket appelé: ${data.ticket?.ticketId} au guichet ${data.counterId}`);
// // //         setLastCalled(data);
// // //         setShowCallAnimation(true);
        
// // //         // Jouer un son si disponible
// // //         const audioElement = document.getElementById('call-sound');
// // //         if (audioElement) {
// // //           audioElement.play().catch(err => {
// // //             addDebugMessage(`Impossible de jouer le son: ${err.message}`);
// // //           });
// // //         }
        
// // //         // Demander une mise à jour après un appel de ticket
// // //         setTimeout(() => requestUpdate(), 1000);
        
// // //         // Cacher l'animation après quelques secondes
// // //         const duration = config?.display?.ticketCallingDuration || 10;
// // //         setTimeout(() => {
// // //           setShowCallAnimation(false);
// // //         }, duration * 1000);
// // //       });
      
// // //       socket.on('newTicket', (ticket) => {
// // //         addDebugMessage(`Nouveau ticket créé: ${ticket.ticketId} pour le service ${ticket.serviceType}`);
// // //         // Demander une mise à jour après création d'un nouveau ticket
// // //         setTimeout(() => requestUpdate(), 1000);
// // //       });
      
// // //       socket.on('connectToDisplay', () => {
// // //         addDebugMessage('Connexion à l\'écran d\'affichage confirmée');
// // //         // Demander une mise à jour après connexion confirmée
// // //         setTimeout(() => requestUpdate(), 1000);
// // //       });
// // //     };
    
// // //     // S'enregistrer comme client d'affichage
// // //     if (socket && connected) {
// // //       addDebugMessage('Socket connecté, enregistrement comme écran d\'affichage');
// // //       registerClient('display');
      
// // //       // Configurer les écouteurs d'événements
// // //       setupSocketListeners();
// // //     }
    
// // //     // Récupérer les données manuellement si le Socket n'est pas connecté
// // //     if (!connected && !loading && config) {
// // //       addDebugMessage('Socket non connecté, récupération des données via API REST');
// // //       fetchTicketsManually();
// // //     }
    
// // //     // Demander périodiquement des mises à jour
// // //     const updateInterval = setInterval(() => {
// // //       addDebugMessage('Mise à jour périodique programmée');
// // //       requestUpdate();
// // //     }, 30000); // Toutes les 30 secondes
    
// // //     return () => {
// // //       if (socket) {
// // //         addDebugMessage('Nettoyage des écouteurs d\'événements Socket.IO');
// // //         socket.off('initialData');
// // //         socket.off('displayUpdate');
// // //         socket.off('ticketCalled');
// // //         socket.off('newTicket');
// // //         socket.off('connectToDisplay');
// // //       }
// // //       clearInterval(updateInterval);
// // //     };
// // //   }, [socket, connected, config, loading, registerClient, updateDisplayData, requestUpdate, fetchTicketsManually, addDebugMessage]);
  
// // //   // Gérer le début de l'appui long pour le mode débogage
// // //   const handleDebugTouchStart = useCallback(() => {
// // //     debugTimerRef.current = setTimeout(() => {
// // //       setDebugMode(!debugMode);
// // //       addDebugMessage('Mode débogage ' + (!debugMode ? 'activé' : 'désactivé'));
// // //     }, 5000);
// // //   }, [debugMode, addDebugMessage]);
  
// // //   // Gérer la fin de l'appui pour éviter l'activation accidentelle
// // //   const handleDebugTouchEnd = useCallback(() => {
// // //     if (debugTimerRef.current) {
// // //       clearTimeout(debugTimerRef.current);
// // //     }
// // //   }, []);

// // //   // Si les données sont en cours de chargement
// // //   if (loading) {
// // //     return (
// // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // //         <div className="p-8 bg-white rounded-lg shadow-md text-center">
// // //           <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
// // //           <p className="text-gray-700">Chargement...</p>
// // //         </div>
// // //       </div>
// // //     );
// // //   }

// // //   // Si une erreur s'est produite
// // //   if (error || !config) {
// // //     return (
// // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // //         <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
// // //           <div className="text-red-500 text-6xl mb-4">??</div>
// // //           <h1 className="text-xl font-bold mb-4">Erreur</h1>
// // //           <p className="mb-6 text-gray-700">{error || "La configuration n'a pas pu être chargée"}</p>
// // //           <button 
// // //             className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
// // //             onClick={() => window.location.reload()}
// // //           >
// // //             Réessayer
// // //           </button>
// // //         </div>
// // //       </div>
// // //     );
// // //   }

// // //   // Couleur principale depuis la configuration
// // //   const primaryColor = config.ui?.primaryColor || "#2196F3";
  
// // //   // Format de l'heure: HH:MM
// // //   const timeString = currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// // //   // Format de la date: jj/mm/aaaa
// // //   const dateString = currentDate.toLocaleDateString('fr-FR', { 
// // //     day: '2-digit', 
// // //     month: '2-digit',
// // //     year: 'numeric' 
// // //   });

// // //   return (
// // //     <div 
// // //       className="min-h-screen flex flex-col overflow-hidden"
// // //       style={{ backgroundColor: primaryColor }}
// // //     >
// // //       {/* Son pour les appels de tickets */}
// // //       <audio id="call-sound" src="/sounds/ding.mp3" preload="auto"></audio>
      
// // //       {/* En-tête */}
// // //       <header className="flex justify-between items-center p-4 bg-opacity-20 bg-black">
// // //         <div className="flex items-center">
// // //           <img 
// // //             src={config.agency?.logo || '/logo.png'} 
// // //             alt={config.agency?.name || 'Logo'} 
// // //             className="h-16 mr-4 bg-white p-2 rounded-lg"
// // //             onError={(e) => {
// // //               e.target.src = '/logo.png'; // Fallback si l'image ne charge pas
// // //               e.target.onerror = null;
// // //             }}
// // //           />
// // //           <div>
// // //             <h1 className="text-2xl font-bold text-white">{config.agency?.name || 'Agence'}</h1>
// // //             <p className="text-white opacity-80">{config.agency?.company || 'Entreprise'}</p>
// // //           </div>
// // //         </div>
// // //         <div className="text-right text-white">
// // //           <div className="text-3xl font-bold">{timeString}</div>
// // //           <div>{dateString}</div>
// // //           {!connected && (
// // //             <div className="text-xs text-red-300 mt-1">Non connecté</div>
// // //           )}
// // //           {lastUpdateTime && (
// // //             <div className="text-xs text-gray-300 mt-1">
// // //               MàJ: {lastUpdateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
// // //             </div>
// // //           )}
// // //         </div>
// // //       </header>
      
// // //       {/* Contenu principal */}
// // //       <main className="flex-1 flex p-4">
// // //         {/* Panneau de gauche: Message de bienvenue */}
// // //         <div className="flex-1 bg-blue-500 rounded-lg p-4 mr-4 flex items-center justify-center shadow-lg">
// // //           <div className="text-center">
// // //             <div className="text-4xl text-white mb-6 rtl" dir="rtl">{config.ui?.welcomeMessage?.ar}</div>
// // //             <div className="text-2xl text-white">{config.ui?.welcomeMessage?.fr}</div>
            
// // //             {/* Afficher le nombre de personnes en attente si activé */}
// // //             {config.display?.showQueueLength && (
// // //               <div className="mt-12 grid grid-cols-3 gap-4">
// // //                 {config.serviceTypes?.map(service => (
// // //                   <div key={service.id} className="bg-white bg-opacity-20 p-3 rounded-lg">
// // //                     <div className="text-white font-bold">{service.name}</div>
// // //                     <div className="text-3xl font-bold text-white mt-2">
// // //                       {waitingCounts[service.id] || 0}
// // //                       <span className="text-sm ml-1">en attente</span>
// // //                     </div>
// // //                   </div>
// // //                 ))}
// // //               </div>
// // //             )}
            
// // //             {/* Bouton pour forcer une mise à jour */}
// // //             <button 
// // //               className="mt-8 px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition"
// // //               onClick={requestUpdate}
// // //             >
// // //               Actualiser les données
// // //             </button>
// // //           </div>
// // //         </div>
        
// // //         {/* Panneau de droite: Tickets en service */}
// // //         <div className="w-96 bg-blue-400 rounded-lg p-4 shadow-lg">
// // //           <h2 className="text-xl font-bold text-white text-center mb-4 border-b border-white border-opacity-20 pb-2">Tickets appelés</h2>
          
// // //           <div className="space-y-3">
// // //             {servingTickets.map((ticket) => {
// // //               const service = config.serviceTypes.find(s => s.id === ticket.serviceType);
// // //               const counter = config.counters.find(c => c.id === ticket.counterId);
              
// // //               return (
// // //                 <div 
// // //                   key={ticket.ticketId} 
// // //                   className="flex justify-between items-center bg-white rounded-lg p-3 mb-3 shadow"
// // //                 >
// // //                   <div className="text-blue-600 font-bold text-3xl">
// // //                     {ticket.ticketId}
// // //                   </div>
// // //                   <div 
// // //                     className="bg-blue-600 text-white w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl"
// // //                     style={{ backgroundColor: service?.color || primaryColor }}
// // //                   >
// // //                     {counter?.id || '?'}
// // //                   </div>
// // //                 </div>
// // //               );
// // //             })}
            
// // //             {/* Si aucun ticket en service */}
// // //             {servingTickets.length === 0 && (
// // //               <div className="text-center py-12 text-white opacity-75">
// // //                 Aucun ticket en service actuellement
// // //               </div>
// // //             )}
// // //           </div>
// // //         </div>
// // //       </main>
      
// // //       {/* Pied de page */}
// // //       <footer className="p-3 bg-opacity-20 bg-black text-white text-center">
// // //         {config.agency?.companySlogan || ''}
        
// // //         {/* Zone tactile invisible pour activer le mode débogage */}
// // //         <div 
// // //           className="absolute bottom-0 right-0 w-16 h-16 opacity-0"
// // //           onTouchStart={handleDebugTouchStart}
// // //           onTouchEnd={handleDebugTouchEnd}
// // //           onClick={() => {}} // Pour éviter les erreurs de lint
// // //         />
// // //       </footer>
      
// // //       {/* Animation d'appel de ticket */}
// // //       {showCallAnimation && lastCalled && (
// // //         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 call-animation">
// // //           <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-md animate-bounce">
// // //             <h2 className="text-xl mb-4">Ticket appelé</h2>
// // //             <div className="text-7xl font-bold mb-4" style={{ color: primaryColor }}>
// // //               {lastCalled.ticket?.ticketId}
// // //             </div>
// // //             <div className="text-2xl">
// // //               Guichet {lastCalled.counterId}
// // //             </div>
// // //           </div>
// // //         </div>
// // //       )}
      
// // //       {/* Panneau de débogage - Visible uniquement en mode débogage */}
// // //       {debugMode && (
// // //         <div className="fixed inset-0 bg-black bg-opacity-90 z-50 overflow-auto">
// // //           <div className="p-4">
// // //             <div className="flex justify-between items-center mb-4">
// // //               <h2 className="text-white text-xl font-bold">Mode débogage</h2>
// // //               <button 
// // //                 className="bg-red-500 text-white px-4 py-2 rounded"
// // //                 onClick={() => setDebugMode(false)}
// // //               >
// // //                 Fermer
// // //               </button>
// // //             </div>
            
// // //             <div className="bg-gray-800 rounded p-4 mb-4">
// // //               <h3 className="text-white font-bold mb-2">État de la connexion</h3>
// // //               <p className="text-white">Socket.IO connecté: {connected ? 'Oui' : 'Non'}</p>
// // //               <p className="text-white">Dernière mise à jour: {lastUpdateTime ? lastUpdateTime.toLocaleString() : 'Jamais'}</p>
// // //             </div>
            
// // //             <div className="bg-gray-800 rounded p-4 mb-4">
// // //               <h3 className="text-white font-bold mb-2">Tickets actifs ({activeTicketsRef.current.length})</h3>
// // //               <div className="grid grid-cols-2 gap-2">
// // //                 <div>
// // //                   <h4 className="text-white font-semibold">En attente:</h4>
// // //                   <ul className="text-white text-sm">
// // //                     {activeTicketsRef.current
// // //                       .filter(t => t.status === 'waiting')
// // //                       .map(t => (
// // //                         <li key={t.ticketId} className="mb-1">
// // //                           {t.ticketId} ({t.serviceType}) - {new Date(t.createdAt).toLocaleTimeString()}
// // //                         </li>
// // //                       ))}
// // //                   </ul>
// // //                 </div>
// // //                 <div>
// // //                   <h4 className="text-white font-semibold">En service:</h4>
// // //                   <ul className="text-white text-sm">
// // //                     {activeTicketsRef.current
// // //                       .filter(t => t.status === 'serving')
// // //                       .map(t => (
// // //                         <li key={t.ticketId} className="mb-1">
// // //                           {t.ticketId} (Guichet {t.counterId}) - {new Date(t.calledAt).toLocaleTimeString()}
// // //                         </li>
// // //                       ))}
// // //                   </ul>
// // //                 </div>
// // //               </div>
// // //             </div>
            
// // //             <div className="bg-gray-800 rounded p-4 mb-4">
// // //               <h3 className="text-white font-bold mb-2">Actions</h3>
// // //               <div className="flex space-x-2">
// // //                 <button 
// // //                   className="bg-blue-500 text-white px-4 py-2 rounded"
// // //                   onClick={requestUpdate}
// // //                 >
// // //                   Forcer mise à jour Socket.IO
// // //                 </button>
// // //                 <button 
// // //                   className="bg-green-500 text-white px-4 py-2 rounded"
// // //                   onClick={fetchTicketsManually}
// // //                 >
// // //                   Récupérer via API REST
// // //                 </button>
// // //                 <button 
// // //                   className="bg-yellow-500 text-white px-4 py-2 rounded"
// // //                   onClick={() => window.location.reload()}
// // //                 >
// // //                   Recharger la page
// // //                 </button>
// // //               </div>
// // //             </div>
            
// // //             <div className="bg-gray-800 rounded p-4">
// // //               <h3 className="text-white font-bold mb-2">Journal de débogage</h3>
// // //               <div className="max-h-64 overflow-y-auto">
// // //                 {debugInfo.map((info, index) => (
// // //                   <div key={index} className="text-white text-sm mb-1">
// // //                     <span className="text-gray-400">{info.time}</span> - {info.message}
// // //                   </div>
// // //                 ))}
// // //               </div>
// // //             </div>
// // //           </div>
// // //         </div>
// // //       )}
// // //     </div>
// // //   );
// // // };

// // // export default DisplayScreen;


// // // // // // // // import React, { useState, useEffect, useCallback } from 'react';
// // // // // // // // import { useConfig } from '../../contexts/ConfigContext';
// // // // // // // // import { useSocket } from '../../contexts/SocketContext';

// // // // // // // // /**
// // // // // // // //  * Écran d'affichage pour la salle d'attente
// // // // // // // //  * Affiche les tickets appelés et les guichets correspondants
// // // // // // // //  */
// // // // // // // // const DisplayScreen = () => {
// // // // // // // //   const { config, loading, error } = useConfig();
// // // // // // // //   const { socket, connected, registerClient } = useSocket();
  
// // // // // // // //   // États
// // // // // // // //   const [currentDate, setCurrentDate] = useState(new Date());
// // // // // // // //   const [servingTickets, setServingTickets] = useState([]);
// // // // // // // //   const [waitingCounts, setWaitingCounts] = useState({});
// // // // // // // //   const [lastCalled, setLastCalled] = useState(null);
// // // // // // // //   const [showCallAnimation, setShowCallAnimation] = useState(false);
// // // // // // // //   const [lastUpdateTime, setLastUpdateTime] = useState(null);

// // // // // // // //   // Mettre à jour la date/heure chaque minute
// // // // // // // //   useEffect(() => {
// // // // // // // //     const timer = setInterval(() => {
// // // // // // // //       setCurrentDate(new Date());
// // // // // // // //     }, 60000);
    
// // // // // // // //     return () => clearInterval(timer);
// // // // // // // //   }, []);

// // // // // // // //   // Fonction pour mettre à jour les données d'affichage
// // // // // // // //   const updateDisplayData = useCallback((data) => {
// // // // // // // //     console.log('Mise à jour des données d\'affichage:', data);
    
// // // // // // // //     if (data.activeTickets) {
// // // // // // // //       // Filtrer les tickets en service
// // // // // // // //       const serving = data.activeTickets.filter(ticket => ticket.status === 'serving');
// // // // // // // //       setServingTickets(serving);
// // // // // // // //       console.log('Tickets en service mis à jour:', serving.length);
      
// // // // // // // //       // Compter les tickets en attente par service
// // // // // // // //       const waiting = data.activeTickets.filter(ticket => ticket.status === 'waiting');
// // // // // // // //       const counts = {};
// // // // // // // //       waiting.forEach(ticket => {
// // // // // // // //         counts[ticket.serviceType] = (counts[ticket.serviceType] || 0) + 1;
// // // // // // // //       });
// // // // // // // //       setWaitingCounts(counts);
// // // // // // // //       console.log('Compteurs de tickets en attente mis à jour:', counts);
// // // // // // // //     }
    
// // // // // // // //     setLastUpdateTime(new Date());
// // // // // // // //   }, []);

// // // // // // // //   // Demander manuellement une mise à jour des données
// // // // // // // //   const requestUpdate = useCallback(() => {
// // // // // // // //     if (socket && connected) {
// // // // // // // //       console.log('Demande de mise à jour des données...');
// // // // // // // //       socket.emit('requestDisplayUpdate');
// // // // // // // //     }
// // // // // // // //   }, [socket, connected]);

// // // // // // // //   // Enregistrer comme client "display" au montage et configurer les écouteurs d'événements
// // // // // // // //   useEffect(() => {
// // // // // // // //     if (socket && connected) {
// // // // // // // //       console.log('Socket connecté, enregistrement comme écran d\'affichage');
// // // // // // // //       registerClient('display');
      
// // // // // // // //       // Demander les données initiales
// // // // // // // //       console.log('Demande des données initiales...');
// // // // // // // //       socket.emit('requestInitialData');
      
// // // // // // // //       // Configurer les écouteurs d'événements
// // // // // // // //       socket.on('initialData', (data) => {
// // // // // // // //         console.log('Données initiales reçues');
// // // // // // // //         updateDisplayData(data);
// // // // // // // //       });
      
// // // // // // // //       socket.on('displayUpdate', (data) => {
// // // // // // // //         console.log('Mise à jour d\'affichage reçue');
// // // // // // // //         updateDisplayData(data);
// // // // // // // //       });
      
// // // // // // // //       socket.on('ticketCalled', (data) => {
// // // // // // // //         console.log('Événement ticketCalled reçu:', data);
// // // // // // // //         setLastCalled(data);
// // // // // // // //         setShowCallAnimation(true);
        
// // // // // // // //         // Jouer un son si disponible
// // // // // // // //         const audioElement = document.getElementById('call-sound');
// // // // // // // //         if (audioElement) {
// // // // // // // //           audioElement.play().catch(err => {
// // // // // // // //             console.warn('Impossible de jouer le son:', err);
// // // // // // // //           });
// // // // // // // //         }
        
// // // // // // // //         // Mettre à jour les données après qu'un ticket a été appelé
// // // // // // // //         requestUpdate();
        
// // // // // // // //         // Cacher l'animation après quelques secondes
// // // // // // // //         const duration = config?.display?.ticketCallingDuration || 10;
// // // // // // // //         setTimeout(() => {
// // // // // // // //           setShowCallAnimation(false);
// // // // // // // //         }, duration * 1000);
// // // // // // // //       });
      
// // // // // // // //       socket.on('ticketCompleted', (ticket) => {
// // // // // // // //         console.log('Événement ticketCompleted reçu:', ticket);
// // // // // // // //         // Mettre à jour la liste des tickets en service
// // // // // // // //         setServingTickets(prev => prev.filter(t => t.ticketId !== ticket.ticketId));
        
// // // // // // // //         // Demander une mise à jour complète pour s'assurer que tout est à jour
// // // // // // // //         requestUpdate();
// // // // // // // //       });
      
// // // // // // // //       socket.on('newTicket', (ticket) => {
// // // // // // // //         console.log('Événement newTicket reçu:', ticket);
// // // // // // // //         // Demander une mise à jour des compteurs
// // // // // // // //         requestUpdate();
// // // // // // // //       });
      
// // // // // // // //       socket.on('connectToDisplay', () => {
// // // // // // // //         console.log('Connexion à l\'écran d\'affichage confirmée');
// // // // // // // //       });
      
// // // // // // // //       // Demander périodiquement des mises à jour
// // // // // // // //       const updateInterval = setInterval(() => {
// // // // // // // //         if (socket && connected) {
// // // // // // // //           requestUpdate();
// // // // // // // //         }
// // // // // // // //       }, 30000); // Toutes les 30 secondes
      
// // // // // // // //       return () => {
// // // // // // // //         console.log('Nettoyage des écouteurs d\'événements Socket.IO');
// // // // // // // //         socket.off('initialData');
// // // // // // // //         socket.off('displayUpdate');
// // // // // // // //         socket.off('ticketCalled');
// // // // // // // //         socket.off('ticketCompleted');
// // // // // // // //         socket.off('newTicket');
// // // // // // // //         socket.off('connectToDisplay');
// // // // // // // //         clearInterval(updateInterval);
// // // // // // // //       };
// // // // // // // //     }
// // // // // // // //   }, [socket, connected, registerClient, config, updateDisplayData, requestUpdate]);

// // // // // // // //   // Si les données sont en cours de chargement
// // // // // // // //   if (loading) {
// // // // // // // //     return (
// // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center">
// // // // // // // //           <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
// // // // // // // //           <p className="text-gray-700">Chargement...</p>
// // // // // // // //         </div>
// // // // // // // //       </div>
// // // // // // // //     );
// // // // // // // //   }

// // // // // // // //   // Si une erreur s'est produite
// // // // // // // //   if (error || !config) {
// // // // // // // //     return (
// // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
// // // // // // // //           <div className="text-red-500 text-6xl mb-4">??</div>
// // // // // // // //           <h1 className="text-xl font-bold mb-4">Erreur</h1>
// // // // // // // //           <p className="mb-6 text-gray-700">{error || "La configuration n'a pas pu être chargée"}</p>
// // // // // // // //           <button 
// // // // // // // //             className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
// // // // // // // //             onClick={() => window.location.reload()}
// // // // // // // //           >
// // // // // // // //             Réessayer
// // // // // // // //           </button>
// // // // // // // //         </div>
// // // // // // // //       </div>
// // // // // // // //     );
// // // // // // // //   }

// // // // // // // //   // Couleur principale depuis la configuration
// // // // // // // //   const primaryColor = config.ui?.primaryColor || "#2196F3";
  
// // // // // // // //   // Format de l'heure: HH:MM
// // // // // // // //   const timeString = currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// // // // // // // //   // Format de la date: jj/mm/aaaa
// // // // // // // //   const dateString = currentDate.toLocaleDateString('fr-FR', { 
// // // // // // // //     day: '2-digit', 
// // // // // // // //     month: '2-digit',
// // // // // // // //     year: 'numeric' 
// // // // // // // //   });

// // // // // // // //   return (
// // // // // // // //     <div 
// // // // // // // //       className="min-h-screen flex flex-col overflow-hidden"
// // // // // // // //       style={{ backgroundColor: primaryColor }}
// // // // // // // //     >
// // // // // // // //       {/* Son pour les appels de tickets */}
// // // // // // // //       <audio id="call-sound" src="/sounds/ding.mp3" preload="auto"></audio>
      
// // // // // // // //       {/* En-tête */}
// // // // // // // //       <header className="flex justify-between items-center p-4 bg-opacity-20 bg-black">
// // // // // // // //         <div className="flex items-center">
// // // // // // // //           <img 
// // // // // // // //             src={config.agency?.logo || '/logo.png'} 
// // // // // // // //             alt={config.agency?.name || 'Logo'} 
// // // // // // // //             className="h-16 mr-4 bg-white p-2 rounded-lg"
// // // // // // // //             onError={(e) => {
// // // // // // // //               e.target.src = '/logo.png'; // Fallback si l'image ne charge pas
// // // // // // // //               e.target.onerror = null;
// // // // // // // //             }}
// // // // // // // //           />
// // // // // // // //           <div>
// // // // // // // //             <h1 className="text-2xl font-bold text-white">{config.agency?.name || 'Agence'}</h1>
// // // // // // // //             <p className="text-white opacity-80">{config.agency?.company || 'Entreprise'}</p>
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //         <div className="text-right text-white">
// // // // // // // //           <div className="text-3xl font-bold">{timeString}</div>
// // // // // // // //           <div>{dateString}</div>
// // // // // // // //           {!connected && (
// // // // // // // //             <div className="text-xs text-red-300 mt-1">Non connecté</div>
// // // // // // // //           )}
// // // // // // // //           {lastUpdateTime && (
// // // // // // // //             <div className="text-xs text-gray-300 mt-1">
// // // // // // // //               MàJ: {lastUpdateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
// // // // // // // //             </div>
// // // // // // // //           )}
// // // // // // // //         </div>
// // // // // // // //       </header>
      
// // // // // // // //       {/* Contenu principal */}
// // // // // // // //       <main className="flex-1 flex p-4">
// // // // // // // //         {/* Panneau de gauche: Message de bienvenue */}
// // // // // // // //         <div className="flex-1 bg-blue-500 rounded-lg p-4 mr-4 flex items-center justify-center shadow-lg">
// // // // // // // //           <div className="text-center">
// // // // // // // //             <div className="text-4xl text-white mb-6 rtl" dir="rtl">{config.ui?.welcomeMessage?.ar}</div>
// // // // // // // //             <div className="text-2xl text-white">{config.ui?.welcomeMessage?.fr}</div>
            
// // // // // // // //             {/* Afficher le nombre de personnes en attente si activé */}
// // // // // // // //             {config.display?.showQueueLength && (
// // // // // // // //               <div className="mt-12 grid grid-cols-3 gap-4">
// // // // // // // //                 {config.serviceTypes?.map(service => (
// // // // // // // //                   <div key={service.id} className="bg-white bg-opacity-20 p-3 rounded-lg">
// // // // // // // //                     <div className="text-white font-bold">{service.name}</div>
// // // // // // // //                     <div className="text-3xl font-bold text-white mt-2">
// // // // // // // //                       {waitingCounts[service.id] || 0}
// // // // // // // //                       <span className="text-sm ml-1">en attente</span>
// // // // // // // //                     </div>
// // // // // // // //                   </div>
// // // // // // // //                 ))}
// // // // // // // //               </div>
// // // // // // // //             )}
            
// // // // // // // //             {/* Bouton pour forcer une mise à jour (uniquement visible si dernière mise à jour > 60s) */}
// // // // // // // //             {lastUpdateTime && (new Date() - lastUpdateTime > 60000) && (
// // // // // // // //               <button 
// // // // // // // //                 className="mt-8 px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition"
// // // // // // // //                 onClick={requestUpdate}
// // // // // // // //               >
// // // // // // // //                 Actualiser les données
// // // // // // // //               </button>
// // // // // // // //             )}
// // // // // // // //           </div>
// // // // // // // //         </div>
        
// // // // // // // //         {/* Panneau de droite: Tickets en service */}
// // // // // // // //         <div className="w-96 bg-blue-400 rounded-lg p-4 shadow-lg">
// // // // // // // //           <h2 className="text-xl font-bold text-white text-center mb-4 border-b border-white border-opacity-20 pb-2">Tickets appelés</h2>
          
// // // // // // // //           <div className="space-y-3">
// // // // // // // //             {servingTickets.map((ticket) => {
// // // // // // // //               const service = config.serviceTypes.find(s => s.id === ticket.serviceType);
// // // // // // // //               const counter = config.counters.find(c => c.id === ticket.counterId);
              
// // // // // // // //               return (
// // // // // // // //                 <div 
// // // // // // // //                   key={ticket.ticketId} 
// // // // // // // //                   className="flex justify-between items-center bg-white rounded-lg p-3 mb-3 shadow"
// // // // // // // //                 >
// // // // // // // //                   <div className="text-blue-600 font-bold text-3xl">
// // // // // // // //                     {ticket.ticketId}
// // // // // // // //                   </div>
// // // // // // // //                   <div 
// // // // // // // //                     className="bg-blue-600 text-white w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl"
// // // // // // // //                     style={{ backgroundColor: service?.color || primaryColor }}
// // // // // // // //                   >
// // // // // // // //                     {counter?.id || '?'}
// // // // // // // //                   </div>
// // // // // // // //                 </div>
// // // // // // // //               );
// // // // // // // //             })}
            
// // // // // // // //             {/* Si aucun ticket en service */}
// // // // // // // //             {servingTickets.length === 0 && (
// // // // // // // //               <div className="text-center py-12 text-white opacity-75">
// // // // // // // //                 Aucun ticket en service actuellement
// // // // // // // //               </div>
// // // // // // // //             )}
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //       </main>
      
// // // // // // // //       {/* Pied de page */}
// // // // // // // //       <footer className="p-3 bg-opacity-20 bg-black text-white text-center">
// // // // // // // //         {config.agency?.companySlogan || ''}
// // // // // // // //       </footer>
      
// // // // // // // //       {/* Animation d'appel de ticket */}
// // // // // // // //       {showCallAnimation && lastCalled && (
// // // // // // // //         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 call-animation">
// // // // // // // //           <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-md animate-bounce">
// // // // // // // //             <h2 className="text-xl mb-4">Ticket appelé</h2>
// // // // // // // //             <div className="text-7xl font-bold mb-4" style={{ color: primaryColor }}>
// // // // // // // //               {lastCalled.ticket?.ticketId}
// // // // // // // //             </div>
// // // // // // // //             <div className="text-2xl">
// // // // // // // //               Guichet {lastCalled.counterId}
// // // // // // // //             </div>
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //       )}
// // // // // // // //     </div>
// // // // // // // //   );
// // // // // // // // };

// // // // // // // // export default DisplayScreen;




// // // // // // // // import React, { useState, useEffect } from 'react';
// // // // // // // // import { useConfig } from '../../contexts/ConfigContext';
// // // // // // // // import { useSocket } from '../../contexts/SocketContext';

// // // // // // // // /**
// // // // // // // //  * Écran d'affichage pour la salle d'attente
// // // // // // // //  * Affiche les tickets appelés et les guichets correspondants
// // // // // // // //  */
// // // // // // // // const DisplayScreen = () => {
// // // // // // // //   const { config, loading, error } = useConfig();
// // // // // // // //   const { socket, registerClient } = useSocket();
  
// // // // // // // //   // États
// // // // // // // //   const [currentDate, setCurrentDate] = useState(new Date());
// // // // // // // //   const [servingTickets, setServingTickets] = useState([]);
// // // // // // // //   const [waitingCounts, setWaitingCounts] = useState({});
// // // // // // // //   const [lastCalled, setLastCalled] = useState(null);
// // // // // // // //   const [showCallAnimation, setShowCallAnimation] = useState(false);

// // // // // // // //   // Mettre à jour la date/heure chaque minute
// // // // // // // //   useEffect(() => {
// // // // // // // //     const timer = setInterval(() => {
// // // // // // // //       setCurrentDate(new Date());
// // // // // // // //     }, 60000);
    
// // // // // // // //     return () => clearInterval(timer);
// // // // // // // //   }, []);

// // // // // // // //   // Enregistrer comme client "display" au montage
// // // // // // // //   useEffect(() => {
// // // // // // // //     if (socket) {
// // // // // // // //       registerClient('display');
      
// // // // // // // //       // Récupérer les données initiales
// // // // // // // //       socket.on('initialData', (data) => {
// // // // // // // //         if (data.activeTickets) {
// // // // // // // //           // Filtrer les tickets en service
// // // // // // // //           const serving = data.activeTickets.filter(ticket => ticket.status === 'serving');
// // // // // // // //           setServingTickets(serving);
          
// // // // // // // //           // Compter les tickets en attente par service
// // // // // // // //           const waiting = data.activeTickets.filter(ticket => ticket.status === 'waiting');
// // // // // // // //           const counts = {};
// // // // // // // //           waiting.forEach(ticket => {
// // // // // // // //             counts[ticket.serviceType] = (counts[ticket.serviceType] || 0) + 1;
// // // // // // // //           });
// // // // // // // //           setWaitingCounts(counts);
// // // // // // // //         }
// // // // // // // //       });
      
// // // // // // // //       // Écouter les mises à jour périodiques
// // // // // // // //       socket.on('displayUpdate', (data) => {
// // // // // // // //         if (data.activeTickets) {
// // // // // // // //           // Filtrer les tickets en service
// // // // // // // //           const serving = data.activeTickets.filter(ticket => ticket.status === 'serving');
// // // // // // // //           setServingTickets(serving);
          
// // // // // // // //           // Compter les tickets en attente par service
// // // // // // // //           const waiting = data.activeTickets.filter(ticket => ticket.status === 'waiting');
// // // // // // // //           const counts = {};
// // // // // // // //           waiting.forEach(ticket => {
// // // // // // // //             counts[ticket.serviceType] = (counts[ticket.serviceType] || 0) + 1;
// // // // // // // //           });
// // // // // // // //           setWaitingCounts(counts);
// // // // // // // //         }
// // // // // // // //       });
      
// // // // // // // //       // Écouter les appels de tickets
// // // // // // // //       socket.on('ticketCalled', (data) => {
// // // // // // // //         setLastCalled(data);
// // // // // // // //         setShowCallAnimation(true);
        
// // // // // // // //         // Jouer un son si disponible
// // // // // // // //         const audioElement = document.getElementById('call-sound');
// // // // // // // //         if (audioElement) {
// // // // // // // //           audioElement.play().catch(err => {
// // // // // // // //             console.warn('Impossible de jouer le son:', err);
// // // // // // // //           });
// // // // // // // //         }
        
// // // // // // // //         // Cacher l'animation après quelques secondes
// // // // // // // //         const duration = config?.display?.ticketCallingDuration || 10;
// // // // // // // //         setTimeout(() => {
// // // // // // // //           setShowCallAnimation(false);
// // // // // // // //         }, duration * 1000);
// // // // // // // //       });
      
// // // // // // // //       // Écouter les fins de service
// // // // // // // //       socket.on('ticketCompleted', (ticket) => {
// // // // // // // //         // Mettre à jour la liste des tickets en service
// // // // // // // //         setServingTickets(prev => prev.filter(t => t.ticketId !== ticket.ticketId));
// // // // // // // //       });
// // // // // // // //     }
    
// // // // // // // //     return () => {
// // // // // // // //       if (socket) {
// // // // // // // //         socket.off('initialData');
// // // // // // // //         socket.off('displayUpdate');
// // // // // // // //         socket.off('ticketCalled');
// // // // // // // //         socket.off('ticketCompleted');
// // // // // // // //       }
// // // // // // // //     };
// // // // // // // //   }, [socket, registerClient, config]);

// // // // // // // //   // Si les données sont en cours de chargement
// // // // // // // //   if (loading) {
// // // // // // // //     return (
// // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center">
// // // // // // // //           <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
// // // // // // // //           <p className="text-gray-700">Chargement...</p>
// // // // // // // //         </div>
// // // // // // // //       </div>
// // // // // // // //     );
// // // // // // // //   }

// // // // // // // //   // Si une erreur s'est produite
// // // // // // // //   if (error || !config) {
// // // // // // // //     return (
// // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-blue-600">
// // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
// // // // // // // //           <div className="text-red-500 text-6xl mb-4">??</div>
// // // // // // // //           <h1 className="text-xl font-bold mb-4">Erreur</h1>
// // // // // // // //           <p className="mb-6 text-gray-700">{error || "La configuration n'a pas pu être chargée"}</p>
// // // // // // // //           <button 
// // // // // // // //             className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
// // // // // // // //             onClick={() => window.location.reload()}
// // // // // // // //           >
// // // // // // // //             Réessayer
// // // // // // // //           </button>
// // // // // // // //         </div>
// // // // // // // //       </div>
// // // // // // // //     );
// // // // // // // //   }

// // // // // // // //   // Couleur principale depuis la configuration
// // // // // // // //   const primaryColor = config.ui?.primaryColor || "#2196F3";
  
// // // // // // // //   // Format de l'heure: HH:MM
// // // // // // // //   const timeString = currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// // // // // // // //   // Format de la date: jj/mm/aaaa
// // // // // // // //   const dateString = currentDate.toLocaleDateString('fr-FR', { 
// // // // // // // //     day: '2-digit', 
// // // // // // // //     month: '2-digit',
// // // // // // // //     year: 'numeric' 
// // // // // // // //   });

// // // // // // // //   return (
// // // // // // // //     <div 
// // // // // // // //       className="min-h-screen flex flex-col overflow-hidden"
// // // // // // // //       style={{ backgroundColor: primaryColor }}
// // // // // // // //     >
// // // // // // // //       {/* Son pour les appels de tickets */}
// // // // // // // //       <audio id="call-sound" src="/sounds/ding.mp3" preload="auto"></audio>
      
// // // // // // // //       {/* En-tête */}
// // // // // // // //       <header className="flex justify-between items-center p-4 bg-opacity-20 bg-black">
// // // // // // // //         <div className="flex items-center">
// // // // // // // //           <img 
// // // // // // // //             src={config.agency?.logo || '/logo.png'} 
// // // // // // // //             alt={config.agency?.name || 'Logo'} 
// // // // // // // //             className="h-16 mr-4 bg-white p-2 rounded-lg"
// // // // // // // //             onError={(e) => {
// // // // // // // //               e.target.src = '/logo.png'; // Fallback si l'image ne charge pas
// // // // // // // //               e.target.onerror = null;
// // // // // // // //             }}
// // // // // // // //           />
// // // // // // // //           <div>
// // // // // // // //             <h1 className="text-2xl font-bold text-white">{config.agency?.name || 'Agence'}</h1>
// // // // // // // //             <p className="text-white opacity-80">{config.agency?.company || 'Entreprise'}</p>
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //         <div className="text-right text-white">
// // // // // // // //           <div className="text-3xl font-bold">{timeString}</div>
// // // // // // // //           <div>{dateString}</div>
// // // // // // // //         </div>
// // // // // // // //       </header>
      
// // // // // // // //       {/* Contenu principal */}
// // // // // // // //       <main className="flex-1 flex p-4">
// // // // // // // //         {/* Panneau de gauche: Message de bienvenue */}
// // // // // // // //         <div className="flex-1 bg-blue-500 rounded-lg p-4 mr-4 flex items-center justify-center shadow-lg">
// // // // // // // //           <div className="text-center">
// // // // // // // //             <div className="text-4xl text-white mb-6 rtl" dir="rtl">{config.ui?.welcomeMessage?.ar}</div>
// // // // // // // //             <div className="text-2xl text-white">{config.ui?.welcomeMessage?.fr}</div>
            
// // // // // // // //             {/* Afficher le nombre de personnes en attente si activé */}
// // // // // // // //             {config.display?.showQueueLength && (
// // // // // // // //               <div className="mt-12 grid grid-cols-3 gap-4">
// // // // // // // //                 {config.serviceTypes?.map(service => (
// // // // // // // //                   <div key={service.id} className="bg-white bg-opacity-20 p-3 rounded-lg">
// // // // // // // //                     <div className="text-white font-bold">{service.name}</div>
// // // // // // // //                     <div className="text-3xl font-bold text-white mt-2">
// // // // // // // //                       {waitingCounts[service.id] || 0}
// // // // // // // //                       <span className="text-sm ml-1">en attente</span>
// // // // // // // //                     </div>
// // // // // // // //                   </div>
// // // // // // // //                 ))}
// // // // // // // //               </div>
// // // // // // // //             )}
// // // // // // // //           </div>
// // // // // // // //         </div>
        
// // // // // // // //         {/* Panneau de droite: Tickets en service */}
// // // // // // // //         <div className="w-96 bg-blue-400 rounded-lg p-4 shadow-lg">
// // // // // // // //           <h2 className="text-xl font-bold text-white text-center mb-4 border-b border-white border-opacity-20 pb-2">Tickets appelés</h2>
          
// // // // // // // //           <div className="space-y-3">
// // // // // // // //             {servingTickets.map((ticket) => {
// // // // // // // //               const service = config.serviceTypes.find(s => s.id === ticket.serviceType);
// // // // // // // //               const counter = config.counters.find(c => c.id === ticket.counterId);
              
// // // // // // // //               return (
// // // // // // // //                 <div 
// // // // // // // //                   key={ticket.ticketId} 
// // // // // // // //                   className="flex justify-between items-center bg-white rounded-lg p-3 mb-3 shadow"
// // // // // // // //                 >
// // // // // // // //                   <div className="text-blue-600 font-bold text-3xl">
// // // // // // // //                     {ticket.ticketId}
// // // // // // // //                   </div>
// // // // // // // //                   <div 
// // // // // // // //                     className="bg-blue-600 text-white w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl"
// // // // // // // //                     style={{ backgroundColor: service?.color || primaryColor }}
// // // // // // // //                   >
// // // // // // // //                     {counter?.id || '?'}
// // // // // // // //                   </div>
// // // // // // // //                 </div>
// // // // // // // //               );
// // // // // // // //             })}
            
// // // // // // // //             {/* Si aucun ticket en service */}
// // // // // // // //             {servingTickets.length === 0 && (
// // // // // // // //               <div className="text-center py-12 text-white opacity-75">
// // // // // // // //                 Aucun ticket en service actuellement
// // // // // // // //               </div>
// // // // // // // //             )}
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //       </main>
      
// // // // // // // //       {/* Pied de page */}
// // // // // // // //       <footer className="p-3 bg-opacity-20 bg-black text-white text-center">
// // // // // // // //         {config.agency?.companySlogan || ''}
// // // // // // // //       </footer>
      
// // // // // // // //       {/* Animation d'appel de ticket */}
// // // // // // // //       {showCallAnimation && lastCalled && (
// // // // // // // //         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 call-animation">
// // // // // // // //           <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-md animate-bounce">
// // // // // // // //             <h2 className="text-xl mb-4">Ticket appelé</h2>
// // // // // // // //             <div className="text-7xl font-bold mb-4" style={{ color: primaryColor }}>
// // // // // // // //               {lastCalled.ticket?.ticketId}
// // // // // // // //             </div>
// // // // // // // //             <div className="text-2xl">
// // // // // // // //               Guichet {lastCalled.counterId}
// // // // // // // //             </div>
// // // // // // // //           </div>
// // // // // // // //         </div>
// // // // // // // //       )}
// // // // // // // //     </div>
// // // // // // // //   );
// // // // // // // // };

// // // // // // // // export default DisplayScreen;
